import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import FormField from "../../../../components/common/FormField";
import {
  leadDetailsFormData,
  leadDeliveryDetailsFormData,
} from "../../lead/leadAllFields";

interface StorageLeadFormDetailsProps {
  leadDetails: any;
}

const StorageLeadFormDetails: React.FC<StorageLeadFormDetailsProps> = ({
  leadDetails,
}) => {
  const collectionAddresses = leadDetails?.lead?.collection_address || [
    {
      building_number: "",
      street: "",
      town: "",
      county: "",
      postcode: "",
      country: "",
      purchaseStatus: "",
      houseSize: "",
      distance: 0,
      volume: 0,
      volumeUnit: "m3",
    },
  ];

  const deliveryAddresses = leadDetails?.lead?.delivery_address || [
    {
      building_number: "",
      street: "",
      town: "",
      county: "",
      postcode: "",
      country: "",
      purchaseStatus: "",
      houseSize: "",
      distance: 0,
      volume: 0,
      volumeUnit: "m3",
    },
  ];

  const [collectionTab, setCollectionTab] = useState(0);
  const [deliveryTab, setDeliveryTab] = useState(0);

  return (
    <>
      <Accordion
        defaultExpanded
        sx={{
          width: "100%",
          padding: "0px",
          border: "none",
          boxShadow: "none",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMore />}
          sx={{
            padding: "0px 10px",
            border: "none",
            minHeight: "0px !important",
            "& .MuiAccordionSummary-content": {
              margin: "0px !important",
            },
            "&.Mui-expanded .MuiAccordionSummary-content": {
              margin: "0px !important",
            },
          }}
        >
          <Typography
            align="left"
            sx={{
              textAlign: "left",
              padding: "0px",
              fontWeight: 800,
              color: "#333333",
              fontSize: "16px",
            }}
            variant="subtitle1"
          >
            Customer Notes
          </Typography>
        </AccordionSummary>

        <AccordionDetails sx={{ padding: "0px 6px" }}>
          <Grid container spacing={2} sx={{ width: "100% !important" }}>
            <Grid
              item
              xs={12}
              sm={12}
              style={{
                padding: "0px !important",
                paddingTop: "0px !important",
                borderBottom: "1px solid #C4CDD5",
              }}
            >
              <Typography
                align="left"
                sx={{
                  textAlign: "left",
                  padding: "0px",
                  fontWeight: 500,
                  color: "#444444",
                  "&::first-letter": {
                    textTransform: "uppercase",
                  },
                }}
                variant="subtitle1"
              >
                {leadDetails?.lead?.customer_notes}
              </Typography>
            </Grid>
          </Grid>

          {/* ─── Collection Addresses ─── */}
          <Grid
            container
            sx={{
              width: "100% !important",
              marginTop: "10px",
              padding: "0px !important",
              border: "1px solid #C4CDD5",
              borderRadius: "5px",
              overflow: "hidden",
            }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              style={{
                backgroundColor: "#e5f4f1",
                padding: "4px !important",
                marginBottom: "10px",
              }}
            >
              <Tabs
                value={collectionTab}
                onChange={(e, val) => setCollectionTab(val)}
                aria-label="Collection Address Tabs"
                TabIndicatorProps={{ style: { display: "none" } }}
                sx={{
                  minHeight: "20px",
                  "& .MuiTabs-flexContainer": { gap: 1 },
                  padding: "4px 6px",
                }}
              >
                {collectionAddresses?.map((_: any, index: number) => (
                  <Tab
                    sx={{
                      textAlign: "left",
                      padding: "6px 4px",
                      fontWeight: 600,
                      color: "#333333",
                      textTransform: "capitalize",
                      background:
                        collectionTab === index ? "#fff" : "transparent",
                      borderRadius: "5px",
                      minHeight: "32px",
                    }}
                    key={index}
                    label={`Collection ${index + 1}`}
                  />
                ))}
              </Tabs>
            </Grid>

            {collectionAddresses.map(
              (collection: any, index: number) =>
                collectionTab === index && (
                  <React.Fragment key={index}>
                    {leadDetailsFormData.map((field: any, i: number) => {
                      let value = collection[field.id] || "";
                      if (field?.id === "distance" || field?.id === "volume") {
                        value = !value ? 0 : Number(value)?.toFixed(2);
                      }
                      return (
                        <FormField
                          key={`${field.id}-${index}`}
                          onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                            e.preventDefault()
                          }
                          {...{
                            ...field,
                            value,
                            disabled: true,
                            variant: "outlined",
                            isNonMarginTop: true,
                            InputProps: {
                              sx:
                                field.type === "text" ||
                                field.type === "password"
                                  ? { height: "36px" }
                                  : { padding: "6px 12px" },
                            },
                          }}
                        />
                      );
                    })}
                  </React.Fragment>
                )
            )}
          </Grid>

          {/* ─── Delivery Addresses ─── */}
          <Grid
            container
            sx={{
              width: "100% !important",
              padding: "0px !important",
              marginTop: "10px",
              border: "1px solid #C4CDD5",
              borderRadius: "5px",
              overflow: "hidden",
            }}
          >
            <Grid
              item
              xs={12}
              sm={12}
              style={{
                backgroundColor: "#e5f4f1",
                padding: "0px !important",
                marginBottom: "10px",
              }}
            >
              <Tabs
                value={deliveryTab}
                onChange={(e, val) => setDeliveryTab(val)}
                aria-label="Delivery Address Tabs"
                TabIndicatorProps={{ style: { display: "none" } }}
                sx={{
                  minHeight: "20px",
                  "& .MuiTabs-flexContainer": { gap: 1 },
                  padding: "4px 6px",
                }}
              >
                {deliveryAddresses?.map((_: any, index: number) => (
                  <Tab
                    sx={{
                      textAlign: "left",
                      padding: "6px 4px",
                      fontWeight: 600,
                      color: "#333333",
                      textTransform: "capitalize",
                      background:
                        deliveryTab === index ? "#fff" : "transparent",
                      borderRadius: "5px",
                      minHeight: "32px",
                    }}
                    key={index}
                    label={`Delivery ${index + 1}`}
                  />
                ))}
              </Tabs>
            </Grid>

            {deliveryAddresses.map(
              (delivery: any, index: number) =>
                deliveryTab === index && (
                  <React.Fragment key={index}>
                    {leadDeliveryDetailsFormData.map((field: any, i: number) => {
                      const value =
                        field.id === "returning_distance"
                          ? leadDetails?.lead?.returning_distance || "0.00"
                          : delivery[field.id] || "";
                      return (
                        <FormField
                          key={`${field.id}-${index}`}
                          onPaste={(e: React.ClipboardEvent<HTMLInputElement>) =>
                            e.preventDefault()
                          }
                          {...{
                            ...field,
                            value,
                            disabled: true,
                            variant: "outlined",
                            isNonMarginTop: true,
                            InputProps: {
                              sx:
                                field.type === "text" ||
                                field.type === "password"
                                  ? { height: "36px" }
                                  : { padding: "6px 12px" },
                            },
                          }}
                        />
                      );
                    })}
                  </React.Fragment>
                )
            )}
          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  );
};

export default StorageLeadFormDetails;
